import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import { useNavigate } from "react-router-dom";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";
import { loadThemeImage } from "../../../utils/themeAsset";
import { computePuzzleGrid } from "../../../utils/puzzleGrid";
import { fitPieceSize } from "../../../utils/pieceSize";

const PUZZLE_PARTS_MIN = 1;
const PUZZLE_PARTS_MAX = 100;
const PUZZLE_PIECE_PRESETS = [16, 25, 36, 64, 100];
const PUZZLE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const GAME_TIMER_MIN_MINUTES = 0;
const GAME_TIMER_MAX_MINUTES = 30;
const GAME_TIMER_DEFAULT_MINUTES = 5;
const GAME_TIMER_MIN_SECONDS = 0;
const GAME_TIMER_MAX_SECONDS = 59;
const GAME_TIMER_DEFAULT_SECONDS = 0;

function clampGameTimerMinutes(value, fallback = GAME_TIMER_DEFAULT_MINUTES) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, GAME_TIMER_MIN_MINUTES), GAME_TIMER_MAX_MINUTES);
  }
  return Math.min(Math.max(val, GAME_TIMER_MIN_MINUTES), GAME_TIMER_MAX_MINUTES);
}

function clampGameTimerSeconds(value, fallback = GAME_TIMER_DEFAULT_SECONDS) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, GAME_TIMER_MIN_SECONDS), GAME_TIMER_MAX_SECONDS);
  }
  return Math.min(Math.max(val, GAME_TIMER_MIN_SECONDS), GAME_TIMER_MAX_SECONDS);
}

function parsePuzzleImgArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((p) => String(p ?? "").trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((p) => String(p ?? "").trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

function clampPuzzleParts(value, fallback = 9) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, PUZZLE_PARTS_MIN), PUZZLE_PARTS_MAX);
  }
  return Math.min(Math.max(val, PUZZLE_PARTS_MIN), PUZZLE_PARTS_MAX);
}

function resolvePuzzlePartsCount(themeData) {
  const fromArray = parsePuzzleImgArray(themeData?.img_array).length;
  const totalPuzzles = Math.floor(Number(themeData?.total_puzzles));
  const legacyParts = Math.floor(Number(themeData?.puzzle_parts));

  if (
    Number.isFinite(totalPuzzles) &&
    totalPuzzles >= PUZZLE_PARTS_MIN &&
    totalPuzzles <= PUZZLE_PARTS_MAX
  ) {
    return totalPuzzles;
  }

  if (fromArray >= PUZZLE_PARTS_MIN && fromArray <= PUZZLE_PARTS_MAX) {
    return fromArray;
  }

  if (
    Number.isFinite(legacyParts) &&
    legacyParts >= PUZZLE_PARTS_MIN &&
    legacyParts <= PUZZLE_PARTS_MAX
  ) {
    return legacyParts;
  }

  return 9;
}

function validateImageFile(file, { width, height, label, allowJpeg = false, maxBytes } = {}) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  const isPng = file.type === "image/png" || lower.endsWith(".png");
  const isJpeg =
    allowJpeg &&
    (file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg"));
  if (!isPng && !isJpeg) {
    Swal.fire({
      title: "Invalid File Type",
      text: `Only ${allowJpeg ? "PNG or JPEG" : "PNG"} images${width && height ? ` (${width}×${height} px)` : ""} are accepted.`,
      icon: "error",
    });
    return null;
  }
  if (Number.isFinite(maxBytes) && file.size > maxBytes) {
    const maxMb = Math.round(maxBytes / (1024 * 1024));
    Swal.fire({
      title: "File Too Large",
      text: `${label ? `${label} ` : ""}must be at most ${maxMb} MB.`,
      icon: "error",
    });
    return null;
  }
  return file;
}

function handleImageChange(setter, previewSetter, { width, height, label, allowJpeg = false, maxBytes } = {}) {
  return (e) => {
    const inputEl = e.target;
    const raw = inputEl.files?.[0];
    if (!raw) return;
    const file = validateImageFile(raw, { width, height, label, allowJpeg, maxBytes });
    if (!file) {
      inputEl.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (!dataUrl) {
        inputEl.value = "";
        return;
      }
      const img = new Image();
      img.onload = () => {
        const wantsSize = Number.isFinite(Number(width)) && Number.isFinite(Number(height));
        const ok = !wantsSize || (img.width === width && img.height === height);
        if (!ok && wantsSize) {
          Swal.fire({
            title: "Invalid Image Size",
            text: `Image must be exactly ${width}×${height} px.`,
            icon: "error",
          });
          inputEl.value = "";
          return;
        }
        setter(file);
        previewSetter(dataUrl);
        inputEl.value = "";
      };
      img.onerror = () => {
        Swal.fire({
          title: "Invalid Image",
          text: allowJpeg ? "Could not read this image file." : "Could not read this PNG file.",
          icon: "error",
        });
        inputEl.value = "";
      };
      img.src = String(dataUrl);
    };
    reader.onerror = () => {
      Swal.fire({
        title: "Invalid Image",
        text: allowJpeg ? "Could not read this image file." : "Could not read this PNG file.",
        icon: "error",
      });
      inputEl.value = "";
    };
    reader.readAsDataURL(file);
  };
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load puzzle image for slicing."));
    img.src = src;
  });
}

function normalizePuzzleAssetFilename(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) {
    try {
      const last = new URL(value).pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : "";
    } catch {
      return "";
    }
  }
  return value.replace(/^\/+/, "").split("/").pop() || "";
}

async function fetchRemoteImageAsObjectUrl(imageSrc, { assetFilename, adminToken } = {}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const filename = normalizePuzzleAssetFilename(assetFilename || imageSrc);

  if (backendUrl && filename && adminToken) {
    // Straight from S3 first, then the API proxy. Images above ~4 MB cannot pass through Lambda in one
    // response, so the proxy hands them over in 3 MB slices that loadThemeImage joins.
    return loadThemeImage(filename, adminToken, { publicUrl: s3PublicUrl(filename) });
  }

  if (imageSrc && /^https?:\/\//i.test(imageSrc)) {
    try {
      const res = await fetch(imageSrc);
      if (!res.ok) throw new Error("Failed to load puzzle image.");
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      if (err?.message === "Failed to load puzzle image.") throw err;
      throw new Error(err?.message || "Failed to load puzzle image.");
    }
  }

  throw new Error("Failed to load saved puzzle image. Please try again or re-upload.");
}

function useSavedRemoteImage(filename, hasLocalFile, adminToken) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasLocalFile || !filename || !adminToken) {
      setUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchRemoteImageAsObjectUrl(null, { assetFilename: filename, adminToken })
      .then((fetchedUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(fetchedUrl);
          return;
        }
        setUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return fetchedUrl;
        });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setUrl(null);
        setLoading(false);
        setError(err?.message || "Could not load saved image.");
      });

    return () => {
      cancelled = true;
    };
  }, [filename, hasLocalFile, adminToken]);

  return { url, loading, error };
}

async function resolveImageSourceForSlicing(
  puzzleMainFile,
  puzzleMainPreview,
  savedPuzzleMainObjectUrl,
  { assetFilename, adminToken } = {}
) {
  if (puzzleMainPreview) return puzzleMainPreview;
  if (puzzleMainFile) return URL.createObjectURL(puzzleMainFile);
  if (savedPuzzleMainObjectUrl) return savedPuzzleMainObjectUrl;
  const filename = normalizePuzzleAssetFilename(assetFilename);
  if (filename && adminToken) {
    return fetchRemoteImageAsObjectUrl(null, { assetFilename: filename, adminToken });
  }
  return null;
}

async function resolvePreviewImageSrc(imageSrc, { assetFilename, adminToken } = {}) {
  if (!imageSrc) return null;
  if (imageSrc.startsWith("blob:") || imageSrc.startsWith("data:")) return imageSrc;
  return fetchRemoteImageAsObjectUrl(imageSrc, { assetFilename, adminToken });
}

function buildSliceSizes(total, count) {
  const sizes = [];
  let used = 0;
  for (let i = 0; i < count; i += 1) {
    const size = i < count - 1 ? Math.floor(total / count) : total - used;
    sizes.push(size);
    used += size;
  }
  return sizes;
}

async function sliceImageIntoPieces(imageSrc, parts) {
  const img = await loadImageElement(imageSrc);
  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const { rows, cols } = computePuzzleGrid(parts, aspectRatio);
  const colWidths = buildSliceSizes(img.naturalWidth, cols);
  const rowHeights = buildSliceSizes(img.naturalHeight, rows);

  if (colWidths.some((w) => w < 1) || rowHeights.some((h) => h < 1)) {
    throw new Error("Image is too small to divide into the selected number of pieces.");
  }

  const files = [];
  const dataUrls = [];
  let sy = 0;
  for (let row = 0; row < rows; row++) {
    const pieceHeight = rowHeights[row];
    let sx = 0;
    for (let col = 0; col < cols; col++) {
      const pieceWidth = colWidths[col];
      // Each piece is saved at most MAX_PIECE_SIDE px on its longest side (same shape, smaller file):
      // full-resolution pieces made the reveal page slow to load and too heavy for phones.
      const out = fitPieceSize(pieceWidth, pieceHeight);
      const canvas = document.createElement("canvas");
      canvas.width = out.width;
      canvas.height = out.height;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        img,
        sx,
        sy,
        pieceWidth,
        pieceHeight,
        0,
        0,
        out.width,
        out.height
      );
      sx += pieceWidth;
      dataUrls.push(canvas.toDataURL("image/jpeg", 0.92));
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Failed to create puzzle piece image."))),
          "image/jpeg",
          0.92
        );
      });
      const partIndex = row * cols + col + 1;
      files.push(new File([blob], `puzzle_part_${partIndex}.jpg`, { type: "image/jpeg" }));
    }
    sy += pieceHeight;
  }
  return { files, dataUrls, rows, cols };
}

function PuzzlePartsPreview({
  imageSrc,
  parts,
  savedPieceUrls,
  useSavedPieces = false,
  assetFilename = "",
  adminToken = "",
}) {
  const [aspectRatio, setAspectRatio] = useState(4 / 3);
  const [pieceUrls, setPieceUrls] = useState([]);
  const [gridRows, setGridRows] = useState(3);
  const [gridCols, setGridCols] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { rows, cols } = useMemo(
    () => computePuzzleGrid(parts, aspectRatio),
    [parts, aspectRatio]
  );

  useEffect(() => {
    if (!imageSrc) {
      setAspectRatio(4 / 3);
      return undefined;
    }
    // Only reads width/height, never pixels, so no CORS request is needed. Asking
    // for one made a saved image (still on its direct S3 URL while the proxied copy
    // loads) fail whenever the bucket has no CORS rule for this site.
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = imageSrc;
    return () => {
      img.onload = null;
    };
  }, [imageSrc]);

  const savedPiecesMatchParts =
    useSavedPieces && savedPieceUrls?.length > 0 && savedPieceUrls.length === rows * cols;

  useEffect(() => {
    if (!imageSrc) {
      if (savedPiecesMatchParts) {
        setPieceUrls(savedPieceUrls);
        setGridRows(rows);
        setGridCols(cols);
      } else {
        setPieceUrls([]);
      }
      setLoading(false);
      setError("");
      return undefined;
    }

    if (savedPiecesMatchParts) {
      setPieceUrls(savedPieceUrls);
      setGridRows(rows);
      setGridCols(cols);
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    let sliceBlobUrl = null;
    setLoading(true);
    setError("");
    setPieceUrls([]);

    (async () => {
      try {
        const sliceSrc = await resolvePreviewImageSrc(imageSrc, { assetFilename, adminToken });
        if (cancelled) {
          if (sliceSrc?.startsWith("blob:") && sliceSrc !== imageSrc) {
            URL.revokeObjectURL(sliceSrc);
          }
          return;
        }
        if (sliceSrc?.startsWith("blob:") && sliceSrc !== imageSrc) {
          sliceBlobUrl = sliceSrc;
        }
        const { dataUrls, rows: r, cols: c } = await sliceImageIntoPieces(sliceSrc, parts);
        if (cancelled) return;
        setPieceUrls(dataUrls);
        setGridRows(r);
        setGridCols(c);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setPieceUrls([]);
        setLoading(false);
        setError(err?.message || "Could not preview puzzle pieces.");
      }
    })();

    return () => {
      cancelled = true;
      if (sliceBlobUrl) {
        URL.revokeObjectURL(sliceBlobUrl);
      }
    };
  }, [imageSrc, parts, savedPieceUrls, savedPiecesMatchParts, useSavedPieces, rows, cols, assetFilename, adminToken]);

  const displayUrls = pieceUrls;
  const pieceSize = gridCols > 24 ? 26 : gridCols > 14 ? 34 : gridCols > 8 ? 44 : 60;
  const pieceGap = gridCols > 24 ? 3 : gridCols > 14 ? 4 : 6;

  return (
    <div style={{ padding: 16, background: "#f8f9fa", borderRadius: 10, border: "1px solid #dee2e6" }}>
      <div style={{ fontSize: 13, color: "#6c757d", marginBottom: 10 }}>
        Cut pieces: <strong>{rows} rows × {cols} columns</strong> = {rows * cols} pieces
        {rows * cols !== parts ? ` (adjusted from ${parts} to always keep at least 2 rows)` : ""}
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: "#6c757d", padding: "12px 0" }}>Generating piece preview…</div>
      ) : null}
      {error ? (
        <div style={{ fontSize: 13, color: "#c62828", padding: "8px 0" }}>{error}</div>
      ) : null}
      {displayUrls.length > 0 ? (
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${gridCols}, ${pieceSize}px)`,
              gap: pieceGap,
              width: gridCols * pieceSize + (gridCols - 1) * pieceGap,
            }}
          >
            {displayUrls.map((url, i) => (
              <div
                key={i}
                style={{
                  borderRadius: 6,
                  border: "2px solid #fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                  background: "#e5e7eb",
                }}
              >
                <img
                  src={url}
                  alt="Puzzle piece"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {!loading && !imageSrc && displayUrls.length === 0 ? (
        <div style={{ fontSize: 12, color: "#999", marginTop: 8, fontStyle: "italic" }}>
          Upload an image above to see cut pieces
        </div>
      ) : null}
    </div>
  );
}

export default function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const puzzleMainInputRef = useRef(null);
  const puzzleMasterInputRef = useRef(null);

  const [puzzleMainFile, setPuzzleMainFile] = useState(null);
  const [puzzleMainPreview, setPuzzleMainPreview] = useState(null);
  const [puzzleMasterFile, setPuzzleMasterFile] = useState(null);
  const [puzzleMasterPreview, setPuzzleMasterPreview] = useState(null);
  const [savingPuzzle, setSavingPuzzle] = useState(false);
  const [puzzleParts, setPuzzleParts] = useState(9);
  const [puzzlePartsInput, setPuzzlePartsInput] = useState("9");
  const [puzzlePartsBaseline, setPuzzlePartsBaseline] = useState(9);
  const [customPieceCount, setCustomPieceCount] = useState(!PUZZLE_PIECE_PRESETS.includes(9));
  const [puzzlePiecePreviews, setPuzzlePiecePreviews] = useState([]);
  const [puzzlePiecePreviewParts, setPuzzlePiecePreviewParts] = useState(null);
  const [gameMode, setGameMode] = useState("live");
  const [gameModeBaseline, setGameModeBaseline] = useState("live");
  const [gameTimerMinutes, setGameTimerMinutes] = useState(GAME_TIMER_DEFAULT_MINUTES);
  const [gameTimerMinutesInput, setGameTimerMinutesInput] = useState(String(GAME_TIMER_DEFAULT_MINUTES));
  const [gameTimerMinutesBaseline, setGameTimerMinutesBaseline] = useState(GAME_TIMER_DEFAULT_MINUTES);
  const [gameTimerSeconds, setGameTimerSeconds] = useState(GAME_TIMER_DEFAULT_SECONDS);
  const [gameTimerSecondsInput, setGameTimerSecondsInput] = useState(String(GAME_TIMER_DEFAULT_SECONDS));
  const [gameTimerSecondsBaseline, setGameTimerSecondsBaseline] = useState(GAME_TIMER_DEFAULT_SECONDS);
  const [downloadEnabled, setDownloadEnabled] = useState(true);
  const [downloadEnabledBaseline, setDownloadEnabledBaseline] = useState(true);
  const [revealTitle, setRevealTitle] = useState("");
  const [revealTitleBaseline, setRevealTitleBaseline] = useState("");
  const puzzleSettingsDirtyRef = useRef(false);

  // Once on mount: a successful fetch rewrites state.theme.currentTheme, so depending on it
  // here made every load trigger a second identical fetchThemeData.
  useEffect(() => {
    if (adminToken) dispatch(fetchThemeData({ themeId: currentTheme || null, isAdmin: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, adminToken]);

  puzzleSettingsDirtyRef.current =
    puzzleParts !== puzzlePartsBaseline ||
    Boolean(puzzleMainPreview) ||
    Boolean(puzzleMainFile) ||
    Boolean(puzzleMasterPreview) ||
    Boolean(puzzleMasterFile) ||
    gameMode !== gameModeBaseline ||
    gameTimerMinutes !== gameTimerMinutesBaseline ||
    gameTimerSeconds !== gameTimerSecondsBaseline ||
    downloadEnabled !== downloadEnabledBaseline ||
    revealTitle !== revealTitleBaseline;

  useEffect(() => {
    if (!data) return;
    if (puzzleSettingsDirtyRef.current) return;
    const partsCount = resolvePuzzlePartsCount(data);
    setPuzzleParts(partsCount);
    setPuzzlePartsInput(String(partsCount));
    setPuzzlePartsBaseline(partsCount);
    setCustomPieceCount(!PUZZLE_PIECE_PRESETS.includes(partsCount));
    setPuzzlePiecePreviews([]);
    setPuzzlePiecePreviewParts(null);
    const savedGameMode = data?.game_mode === "self_paced" ? "self_paced" : "live";
    setGameMode(savedGameMode);
    setGameModeBaseline(savedGameMode);
    const savedTimerMinutes = clampGameTimerMinutes(data?.game_timer_minutes);
    setGameTimerMinutes(savedTimerMinutes);
    setGameTimerMinutesInput(String(savedTimerMinutes));
    setGameTimerMinutesBaseline(savedTimerMinutes);
    const savedTimerSeconds = clampGameTimerSeconds(data?.game_timer_seconds);
    setGameTimerSeconds(savedTimerSeconds);
    setGameTimerSecondsInput(String(savedTimerSeconds));
    setGameTimerSecondsBaseline(savedTimerSeconds);
    const savedDownloadEnabled = data?.download_enabled !== "false" && data?.download_enabled !== false;
    setDownloadEnabled(savedDownloadEnabled);
    setDownloadEnabledBaseline(savedDownloadEnabled);
    const savedRevealTitle = data?.reveal_title != null ? String(data.reveal_title) : "";
    setRevealTitle(savedRevealTitle);
    setRevealTitleBaseline(savedRevealTitle);
  }, [data]);

  const savedTotalPuzzles = useMemo(() => {
    if (!data) return null;
    return resolvePuzzlePartsCount(data);
  }, [data]);

  const savedPuzzleImageFilename = useMemo(
    () => normalizePuzzleAssetFilename(data?.puzzle_image),
    [data?.puzzle_image]
  );
  const savedPuzzleMainUrl = useMemo(() => s3PublicUrl(data?.puzzle_image), [data?.puzzle_image]);
  const savedPuzzleMasterImageFilename = useMemo(
    () => normalizePuzzleAssetFilename(data?.puzzle_master_image),
    [data?.puzzle_master_image]
  );
  const savedPuzzleMasterUrl = useMemo(
    () => s3PublicUrl(data?.puzzle_master_image),
    [data?.puzzle_master_image]
  );
  const savedPuzzlePartUrls = useMemo(() => {
    return parsePuzzleImgArray(data?.img_array).map((filename) => s3PublicUrl(filename)).filter(Boolean);
  }, [data?.img_array]);

  const {
    url: savedPuzzleMainObjectUrl,
    loading: savedPuzzleMainLoading,
    error: savedPuzzleMainError,
  } = useSavedRemoteImage(
    savedPuzzleImageFilename,
    Boolean(puzzleMainPreview || puzzleMainFile),
    adminToken
  );
  const {
    url: savedPuzzleMasterObjectUrl,
    loading: savedPuzzleMasterLoading,
    error: savedPuzzleMasterError,
  } = useSavedRemoteImage(
    savedPuzzleMasterImageFilename,
    Boolean(puzzleMasterPreview || puzzleMasterFile),
    adminToken
  );

  const resolvedPuzzleMainSrc = puzzleMainPreview || savedPuzzleMainObjectUrl || savedPuzzleMainUrl;
  const puzzleMainDisplaySrc = puzzleMainPreview || savedPuzzleMainUrl || savedPuzzleMainObjectUrl;
  const resolvedPuzzleMasterSrc = puzzleMasterPreview || savedPuzzleMasterObjectUrl || savedPuzzleMasterUrl;
  const puzzleMasterDisplaySrc = puzzleMasterPreview || savedPuzzleMasterUrl || savedPuzzleMasterObjectUrl;

  const puzzlePartsDirty =
    puzzleParts !== puzzlePartsBaseline || Boolean(puzzleMainPreview) || Boolean(puzzleMainFile);

  const useSavedPuzzlePieces =
    !puzzlePartsDirty &&
    savedPuzzlePartUrls.length > 0 &&
    savedPuzzlePartUrls.length === puzzleParts &&
    puzzleParts === savedTotalPuzzles;

  const handleSavePuzzle = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    const hasMainUpload = Boolean(puzzleMainFile) || Boolean(puzzleMainPreview);
    const hasSavedImage = Boolean(savedPuzzleImageFilename) || Boolean(resolvedPuzzleMainSrc);

    if (!hasMainUpload && !hasSavedImage) {
      Swal.fire("Image required", "Upload an Outline/Reference image before saving.", "info");
      return;
    }

    const hasMasterUpload = Boolean(puzzleMasterFile) || Boolean(puzzleMasterPreview);
    const hasSavedMasterImage =
      Boolean(savedPuzzleMasterImageFilename) || Boolean(resolvedPuzzleMasterSrc);

    if (!hasMasterUpload && !hasSavedMasterImage) {
      Swal.fire("Image required", "Upload a Coloured Master image before saving.", "info");
      return;
    }

    const typedParts = Math.floor(Number(puzzlePartsInput));
    if (
      puzzlePartsInput !== "" &&
      Number.isFinite(typedParts) &&
      (typedParts < PUZZLE_PARTS_MIN || typedParts > PUZZLE_PARTS_MAX)
    ) {
      Swal.fire(
        "Invalid puzzle count",
        `Total puzzles must be between ${PUZZLE_PARTS_MIN} and ${PUZZLE_PARTS_MAX}. Players can outnumber pieces, so extra players share pieces.`,
        "warning"
      );
      return;
    }

    const partsToSave = clampPuzzleParts(puzzleParts);
    if (partsToSave !== puzzleParts) {
      setPuzzleParts(partsToSave);
      setPuzzlePartsInput(String(partsToSave));
    }

    if (gameMode === "live" && gameTimerMinutes * 60 + gameTimerSeconds <= 0) {
      Swal.fire("Invalid timer", "Timer must be at least 1 second.", "warning");
      return;
    }

    let objectUrlToRevoke = null;

    try {
      setSavingPuzzle(true);

      const imageSrc = await resolveImageSourceForSlicing(
        puzzleMainFile,
        puzzleMainPreview,
        savedPuzzleMainObjectUrl,
        { assetFilename: savedPuzzleImageFilename, adminToken }
      );
      if (!imageSrc) {
        throw new Error("No puzzle image available to save.");
      }
      if (
        imageSrc.startsWith("blob:") &&
        imageSrc !== puzzleMainPreview &&
        imageSrc !== savedPuzzleMainObjectUrl
      ) {
        objectUrlToRevoke = imageSrc;
      }

      const { files: partFiles, dataUrls, rows: gridRows, cols: gridCols } = await sliceImageIntoPieces(
        imageSrc,
        partsToSave
      );
      // The image's own shape decides the grid, so the cut may land on a slightly
      // different count than typed (e.g. 11 -> 12 on a wide image). Save what was cut.
      const savedParts = gridRows * gridCols;
      if (savedParts !== puzzleParts) {
        setPuzzleParts(savedParts);
        setPuzzlePartsInput(String(savedParts));
      }
      setPuzzlePiecePreviews(dataUrls);
      setPuzzlePiecePreviewParts(savedParts);

      // Uploaded straight to S3 via presigned URLs rather than bundled into one
      // multipart POST — a full outline + master image plus every sliced piece for
      // both (up to 100 each) easily exceeds API Gateway's hard, non-configurable
      // 10 MB request body limit, which was failing silently as a generic
      // "Failed to fetch" (the oversized request gets rejected before it reaches
      // the Lambda, and before CORS headers are attached).
      const uploadItems = [];

      let mainFile;
      if (puzzleMainFile) {
        mainFile = puzzleMainFile;
      } else {
        const mainBlob = await fetch(imageSrc).then((r) => r.blob());
        const ext = mainBlob.type.includes("png") ? "png" : "jpg";
        mainFile = new File([mainBlob], `puzzle_image.${ext}`, { type: mainBlob.type || "image/jpeg" });
      }
      uploadItems.push({ fieldName: "puzzle_image", file: mainFile });

      partFiles.forEach((file, i) => {
        uploadItems.push({ fieldName: `puzzle_part_${i + 1}`, file });
      });

      if (puzzleMasterFile) {
        uploadItems.push({ fieldName: "puzzle_master_image", file: puzzleMasterFile });
      }

      const masterImageSrc = await resolveImageSourceForSlicing(
        puzzleMasterFile,
        puzzleMasterPreview,
        savedPuzzleMasterObjectUrl,
        { assetFilename: savedPuzzleMasterImageFilename, adminToken }
      );
      if (!masterImageSrc) {
        throw new Error("No coloured master image available to save.");
      }
      // Any size is fine, but the two images must share one SHAPE or the coloured
      // pieces would not line up with the outline pieces they replace.
      const [outlineImg, masterImg] = await Promise.all([
        loadImageElement(imageSrc),
        loadImageElement(masterImageSrc),
      ]);
      const outlineRatio = outlineImg.naturalWidth / outlineImg.naturalHeight;
      const masterRatio = masterImg.naturalWidth / masterImg.naturalHeight;
      if (Math.abs(outlineRatio - masterRatio) / outlineRatio > 0.02) {
        throw new Error(
          `The Outline image (${outlineImg.naturalWidth}×${outlineImg.naturalHeight}) and the Coloured Master image (${masterImg.naturalWidth}×${masterImg.naturalHeight}) must have the same shape (width:height ratio). Sizes can differ, shapes cannot.`
        );
      }

      const { files: masterPartFiles } = await sliceImageIntoPieces(masterImageSrc, savedParts);
      masterPartFiles.forEach((file, i) => {
        uploadItems.push({ fieldName: `master_puzzle_part_${i + 1}`, file });
      });
      if (
        masterImageSrc.startsWith("blob:") &&
        masterImageSrc !== puzzleMasterPreview &&
        masterImageSrc !== savedPuzzleMasterObjectUrl
      ) {
        URL.revokeObjectURL(masterImageSrc);
      }

      const presignRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getPuzzleUploadUrls`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          files: uploadItems.map(({ fieldName, file }) => ({
            fieldName,
            filename: file.name,
            contentType: file.type || "image/jpeg",
          })),
        }),
      });
      const presignJson = await presignRes.json().catch(() => ({}));
      if (!presignRes.ok) {
        throw new Error(presignJson.message || `Failed to prepare upload (status ${presignRes.status})`);
      }
      const uploadUrls = presignJson.urls || {};

      await Promise.all(
        uploadItems.map(async ({ fieldName, file }) => {
          const entry = uploadUrls[fieldName];
          if (!entry?.uploadUrl) {
            throw new Error(`Missing upload URL for ${fieldName}.`);
          }
          // Don't send x-amz-acl here: the S3 presigner (s3Helper.getPresignedUploadUrl)
          // hoists every x-amz-* header it signed — including ACL — out of the headers
          // and into the presigned URL's own query string (see
          // @aws-sdk/signature-v4/moveHeadersToQuery.js), so public-read is already
          // baked into entry.uploadUrl. Sending it again as a duplicate header on the
          // actual PUT is what caused the 403 (S3 rejects a header that conflicts with
          // a same-named signed query param). Content-Type is unaffected — the
          // presigner marks it unsignable, so any value here is fine and S3 just uses
          // it as the object's content type.
          const putRes = await fetch(entry.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type || "image/jpeg" },
            body: file,
          });
          if (!putRes.ok) {
            throw new Error(`Failed to upload ${fieldName} (status ${putRes.status}).`);
          }
        })
      );

      const puzzleKeys = {};
      uploadItems.forEach(({ fieldName }) => {
        puzzleKeys[fieldName] = uploadUrls[fieldName].filename;
      });

      const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          puzzle_keys: puzzleKeys,
          total_puzzles: String(savedParts),
          puzzle_rows: String(gridRows),
          puzzle_cols: String(gridCols),
          game_mode: gameMode,
          ...(gameMode === "live"
            ? {
                game_timer_minutes: String(clampGameTimerMinutes(gameTimerMinutes)),
                game_timer_seconds: String(clampGameTimerSeconds(gameTimerSeconds)),
              }
            : {}),
          download_enabled: String(downloadEnabled),
          reveal_title: revealTitle,
          day: "puzzle",
          currentTheme,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
        }),
      });
      const uploadJson = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok) {
        throw new Error(uploadJson.message || `Puzzle upload failed with status ${uploadRes.status}`);
      }

      Swal.fire("Saved", "Collaborative Canvas cut into pieces and saved successfully.", "success");
      setPuzzleMainFile(null);
      setPuzzleMainPreview(null);
      setPuzzleMasterFile(null);
      setPuzzleMasterPreview(null);
      setPuzzlePartsBaseline(savedParts);
      setGameModeBaseline(gameMode);
      setGameTimerMinutesBaseline(gameTimerMinutes);
      setGameTimerSecondsBaseline(gameTimerSeconds);
      setDownloadEnabledBaseline(downloadEnabled);
      setRevealTitleBaseline(revealTitle);
      setPuzzlePiecePreviews(dataUrls);
      setPuzzlePiecePreviewParts(savedParts);
      if (puzzleMainInputRef.current) {
        puzzleMainInputRef.current.value = "";
      }
      if (puzzleMasterInputRef.current) {
        puzzleMasterInputRef.current.value = "";
      }
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save puzzle settings.", "error");
    } finally {
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
      setSavingPuzzle(false);
    }
  };

  const handlePuzzleMainChange = handleImageChange(
    setPuzzleMainFile,
    (preview) => {
      setPuzzleMainPreview(preview);
      setPuzzlePiecePreviews([]);
      setPuzzlePiecePreviewParts(null);
    },
    {
      allowJpeg: true,
      maxBytes: PUZZLE_IMAGE_MAX_BYTES,
      label: "Outline/Reference image",
    }
  );

  const handlePuzzleMasterChange = handleImageChange(
    setPuzzleMasterFile,
    setPuzzleMasterPreview,
    {
      allowJpeg: true,
      maxBytes: PUZZLE_IMAGE_MAX_BYTES,
      label: "Coloured Master image",
    }
  );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="themeupdate-panel">
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">
            Puzzle Settings
            {savedTotalPuzzles != null ? (
              <span style={{ fontWeight: 400, fontSize: 14, marginLeft: 12, color: "#6c757d" }}>
                (total_puzzles: {savedTotalPuzzles})
              </span>
            ) : null}
          </div>
          <div className="themeupdate-card-body">
            <div style={{ marginBottom: 24 }}>
              <label className="themeupdate-label">Reveal Title</label>
              <div className="themeupdate-help" style={{ marginBottom: 8 }}>
                <span>Shown at the top of the final reveal page once the artwork is complete. Leave blank to show no title.</span>
              </div>
              <input
                type="text"
                className="themeupdate-input"
                style={{ width: "100%", maxWidth: 420 }}
                maxLength={100}
                placeholder="e.g. Your Masterpiece is Complete!"
                value={revealTitle}
                onChange={(e) => setRevealTitle(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: 24 }} data-tour="theme-game-mode">
              <label className="themeupdate-label">Game Mode</label>
              <div className="themeupdate-help" style={{ marginBottom: 8 }}>
                <span>Live keeps players synced in real time. Self-Paced lets each player progress on their own.</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, color: gameMode === "self_paced" ? "#212529" : "#adb5bd", fontWeight: 600 }}>
                    Self-Paced
                  </span>
                  <Switch
                    checked={gameMode === "live"}
                    onChange={(checked) => setGameMode(checked ? "live" : "self_paced")}
                    onColor="#4caf50"
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                  <span style={{ fontSize: 14, color: gameMode === "live" ? "#212529" : "#adb5bd", fontWeight: 600 }}>
                    Live
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, color: "#212529", fontWeight: 600 }}>Download</span>
                  <Switch
                    checked={downloadEnabled}
                    onChange={setDownloadEnabled}
                    onColor="#4caf50"
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </div>
              </div>
              {gameMode === "live" ? (
                <div style={{ marginTop: 16 }} data-tour="theme-timer">
                  <label className="themeupdate-label">Timer</label>
                  <div className="themeupdate-help" style={{ marginBottom: 8 }}>
                    <span>
                      How long players get in Live mode ({GAME_TIMER_MIN_MINUTES}–{GAME_TIMER_MAX_MINUTES} min,
                      {" "}{GAME_TIMER_MIN_SECONDS}–{GAME_TIMER_MAX_SECONDS} sec).
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <input
                      type="number"
                      className="themeupdate-input"
                      min={GAME_TIMER_MIN_MINUTES}
                      max={GAME_TIMER_MAX_MINUTES}
                      value={gameTimerMinutesInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setGameTimerMinutesInput(raw);
                        if (raw === "") return;
                        const val = Math.floor(Number(raw));
                        if (
                          Number.isFinite(val) &&
                          val >= GAME_TIMER_MIN_MINUTES &&
                          val <= GAME_TIMER_MAX_MINUTES
                        ) {
                          setGameTimerMinutes(val);
                        }
                      }}
                      onBlur={() => {
                        const val = clampGameTimerMinutes(gameTimerMinutesInput, gameTimerMinutes);
                        if (
                          gameTimerMinutesInput !== "" &&
                          Number.isFinite(Number(gameTimerMinutesInput)) &&
                          (Math.floor(Number(gameTimerMinutesInput)) < GAME_TIMER_MIN_MINUTES ||
                            Math.floor(Number(gameTimerMinutesInput)) > GAME_TIMER_MAX_MINUTES)
                        ) {
                          void Swal.fire({
                            icon: "warning",
                            title: "Invalid timer",
                            text: `Minutes must be between ${GAME_TIMER_MIN_MINUTES} and ${GAME_TIMER_MAX_MINUTES}.`,
                          });
                        }
                        setGameTimerMinutes(val);
                        setGameTimerMinutesInput(String(val));
                      }}
                      style={{ width: 100 }}
                    />
                    <span style={{ fontSize: 14, color: "#6c757d" }}>minutes</span>
                    <input
                      type="number"
                      className="themeupdate-input"
                      min={GAME_TIMER_MIN_SECONDS}
                      max={GAME_TIMER_MAX_SECONDS}
                      value={gameTimerSecondsInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setGameTimerSecondsInput(raw);
                        if (raw === "") return;
                        const val = Math.floor(Number(raw));
                        if (
                          Number.isFinite(val) &&
                          val >= GAME_TIMER_MIN_SECONDS &&
                          val <= GAME_TIMER_MAX_SECONDS
                        ) {
                          setGameTimerSeconds(val);
                        }
                      }}
                      onBlur={() => {
                        const val = clampGameTimerSeconds(gameTimerSecondsInput, gameTimerSeconds);
                        if (
                          gameTimerSecondsInput !== "" &&
                          Number.isFinite(Number(gameTimerSecondsInput)) &&
                          (Math.floor(Number(gameTimerSecondsInput)) < GAME_TIMER_MIN_SECONDS ||
                            Math.floor(Number(gameTimerSecondsInput)) > GAME_TIMER_MAX_SECONDS)
                        ) {
                          void Swal.fire({
                            icon: "warning",
                            title: "Invalid timer",
                            text: `Seconds must be between ${GAME_TIMER_MIN_SECONDS} and ${GAME_TIMER_MAX_SECONDS}.`,
                          });
                        }
                        setGameTimerSeconds(val);
                        setGameTimerSecondsInput(String(val));
                      }}
                      style={{ width: 100 }}
                    />
                    <span style={{ fontSize: 14, color: "#6c757d" }}>seconds</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 24 }} data-tour="theme-images">
              <div className="themeupdate-badge-upload-box" style={{ flex: "1 1 320px", minWidth: 280 }}>
                <label className="themeupdate-label">Outline/Reference Image</label>
                <input
                  ref={puzzleMainInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handlePuzzleMainChange}
                />
                <div className="themeupdate-help">
                  <span>
                    * PNG or JPEG, any size or shape, max{" "}
                    {PUZZLE_IMAGE_MAX_BYTES / (1024 * 1024)} MB. Used for slicing the image into puzzle pieces.
                  </span>
                </div>
                {savedPuzzleMainLoading ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Loading saved puzzle image…
                  </div>
                ) : null}
                {savedPuzzleMainError && !puzzleMainPreview && !puzzleMainFile ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#c62828" }}>
                    {savedPuzzleMainError}
                  </div>
                ) : null}
                {resolvedPuzzleMainSrc ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{puzzleMainPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={puzzleMainDisplaySrc}
                      alt="Outline/reference puzzle"
                      className="themeupdate-preview-img"
                      style={{ maxWidth: 300, maxHeight: 300 }}
                      onError={(e) => {
                        if (
                          savedPuzzleMainObjectUrl &&
                          e.currentTarget.src !== savedPuzzleMainObjectUrl
                        ) {
                          e.currentTarget.src = savedPuzzleMainObjectUrl;
                        }
                      }}
                    />
                  </div>
                ) : savedPuzzleImageFilename ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Saved puzzle image on file ({savedPuzzleImageFilename}). You can change the piece count below without re-uploading.
                  </div>
                ) : null}
              </div>

              <div className="themeupdate-badge-upload-box" style={{ flex: "1 1 320px", minWidth: 280 }}>
                <label className="themeupdate-label">Coloured Master Image</label>
                <input
                  ref={puzzleMasterInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handlePuzzleMasterChange}
                />
                <div className="themeupdate-help">
                  <span>
                    * PNG or JPEG, any size or shape, max{" "}
                    {PUZZLE_IMAGE_MAX_BYTES / (1024 * 1024)} MB. Used for the live preview and auto-fill.
                  </span>
                </div>
                {savedPuzzleMasterLoading ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Loading saved master image…
                  </div>
                ) : null}
                {savedPuzzleMasterError && !puzzleMasterPreview && !puzzleMasterFile ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#c62828" }}>
                    {savedPuzzleMasterError}
                  </div>
                ) : null}
                {resolvedPuzzleMasterSrc ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{puzzleMasterPreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img
                      src={puzzleMasterDisplaySrc}
                      alt="Coloured master puzzle"
                      className="themeupdate-preview-img"
                      style={{ maxWidth: 300, maxHeight: 300 }}
                      onError={(e) => {
                        if (
                          savedPuzzleMasterObjectUrl &&
                          e.currentTarget.src !== savedPuzzleMasterObjectUrl
                        ) {
                          e.currentTarget.src = savedPuzzleMasterObjectUrl;
                        }
                      }}
                    />
                  </div>
                ) : savedPuzzleMasterImageFilename ? (
                  <div className="themeupdate-help" style={{ marginTop: 10, color: "#6c757d" }}>
                    Saved master image on file ({savedPuzzleMasterImageFilename}).
                  </div>
                ) : null}
              </div>
            </div>

            <div style={{ marginBottom: 24 }} data-tour="theme-pieces">
              <label className="themeupdate-label">Number of Canvas Pieces</label>
              <div className="themeupdate-help" style={{ marginBottom: 8 }}>
                <span>
                  Choose how many pieces to split the full Collaborative Canvas into. The whole image is used with no extra or missing parts.
                  {savedPuzzleImageFilename
                    ? " Change the count and click Save — the saved image will be re-cut automatically."
                    : ""}
                  {savedTotalPuzzles != null ? ` Saved value: ${savedTotalPuzzles}.` : ""}
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 16 }}>
                {PUZZLE_PIECE_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className="themeupdate-btn"
                    onClick={() => {
                      setCustomPieceCount(false);
                      setPuzzleParts(preset);
                      setPuzzlePartsInput(String(preset));
                    }}
                    style={{
                      background: !customPieceCount && puzzleParts === preset ? "#6845D3" : "#e9ecef",
                      color: !customPieceCount && puzzleParts === preset ? "#fff" : "#212529",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 16px",
                      cursor: "pointer",
                    }}
                  >
                    {preset}
                  </button>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn"
                  onClick={() => setCustomPieceCount(true)}
                  style={{
                    background: customPieceCount ? "#6845D3" : "#e9ecef",
                    color: customPieceCount ? "#fff" : "#212529",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 16px",
                    cursor: "pointer",
                  }}
                >
                  Custom grid
                </button>
              </div>
              {customPieceCount ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <input
                    type="number"
                    className="themeupdate-input"
                    min={PUZZLE_PARTS_MIN}
                    max={PUZZLE_PARTS_MAX}
                    value={puzzlePartsInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setPuzzlePartsInput(raw);
                      if (raw === "") return;
                      const val = Math.floor(Number(raw));
                      if (Number.isFinite(val) && val >= PUZZLE_PARTS_MIN && val <= PUZZLE_PARTS_MAX) {
                        setPuzzleParts(val);
                      }
                    }}
                    onBlur={() => {
                      const val = clampPuzzleParts(puzzlePartsInput, puzzleParts);
                      if (
                        puzzlePartsInput !== "" &&
                        Number.isFinite(Number(puzzlePartsInput)) &&
                        (Math.floor(Number(puzzlePartsInput)) < PUZZLE_PARTS_MIN ||
                          Math.floor(Number(puzzlePartsInput)) > PUZZLE_PARTS_MAX)
                      ) {
                        void Swal.fire({
                          icon: "warning",
                          title: "Invalid puzzle count",
                          text: `Total puzzles must be between ${PUZZLE_PARTS_MIN} and ${PUZZLE_PARTS_MAX}.`,
                        });
                      }
                      setPuzzleParts(val);
                      setPuzzlePartsInput(String(val));
                    }}
                    style={{ width: 100 }}
                  />
                  <span style={{ fontSize: 14, color: "#6c757d" }}>pieces</span>
                </div>
              ) : null}
              <PuzzlePartsPreview
                imageSrc={resolvedPuzzleMainSrc}
                parts={puzzleParts}
                useSavedPieces={useSavedPuzzlePieces}
                assetFilename={savedPuzzleImageFilename}
                adminToken={adminToken}
                savedPieceUrls={
                  puzzlePiecePreviews.length > 0 && puzzlePiecePreviewParts === puzzleParts
                    ? puzzlePiecePreviews
                    : savedPuzzlePartUrls
                }
              />
            </div>

            <div
              className="themeupdate-actions"
              style={{ justifyContent: "center", marginTop: 28 }}
              data-tour="theme-save"
            >
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={handleSavePuzzle}
                disabled={savingPuzzle}
              >
                {savingPuzzle ? "Saving…" : "Save Puzzle Settings"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
